/**
 * Service de gestion des notifications backoffice
 * 
 * Gère la récupération et la manipulation des notifications du système
 * (nouvelles réservations, paiements échoués, paiements annulés, etc.)
 */

import { API_BASE_URL } from '../api';

export interface Notification {
  id: number;
  type: string;
  priority: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
  booking_id: number | null;
  voucher_id: number | null;
  extra_data: {
    customer_name?: string;
    customer_email?: string;
    service_name?: string;
    amount?: number;
    reason?: string;
    stripe_payment_intent_id?: string;
    [key: string]: any;
  } | null;
}

export interface NotificationStats {
  total: number;
  unread: number;
  read: number;
  by_type: Record<string, number>;
  by_priority: Record<string, number>;
}

class NotificationService {
  private readonly BASE_URL = `${API_BASE_URL}/api/backoffice/notifications`;

  /**
   * Récupérer le token d'authentification depuis le localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('backoffice_access_token');
  }

  /**
   * Créer les headers avec authentification
   */
  private getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  /**
   * Récupérer les notifications
   */
  async getNotifications(params?: {
    unreadOnly?: boolean;
    typeFilter?: string;
    priorityFilter?: string;
    limit?: number;
    offset?: number;
  }): Promise<Notification[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.unreadOnly) {
      searchParams.append('unread_only', 'true');
    }
    if (params?.typeFilter) {
      searchParams.append('type_filter', params.typeFilter);
    }
    if (params?.priorityFilter) {
      searchParams.append('priority_filter', params.priorityFilter);
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.offset) {
      searchParams.append('offset', params.offset.toString());
    }

    const url = `${this.BASE_URL}?${searchParams.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la récupération des notifications: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Récupérer le nombre de notifications non lues
   */
  async getUnreadCount(): Promise<number> {
    const response = await fetch(`${this.BASE_URL}/unread-count`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération du compteur');
    }

    const data = await response.json();
    return data.count;
  }

  /**
   * Marquer une notification comme lue
   */
  async markAsRead(notificationId: number): Promise<void> {
    const response = await fetch(`${this.BASE_URL}/${notificationId}/mark-read`, {
      method: 'PUT',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors du marquage de la notification #${notificationId}`);
    }
  }

  /**
   * Marquer toutes les notifications comme lues
   */
  async markAllAsRead(): Promise<void> {
    const response = await fetch(`${this.BASE_URL}/mark-all-read`, {
      method: 'PUT',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors du marquage de toutes les notifications');
    }
  }

  /**
   * Supprimer une notification
   */
  async deleteNotification(notificationId: number): Promise<void> {
    const response = await fetch(`${this.BASE_URL}/${notificationId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Erreur lors de la suppression de la notification #${notificationId}`);
    }
  }

  /**
   * Récupérer les statistiques des notifications
   */
  async getStats(): Promise<NotificationStats> {
    const response = await fetch(`${this.BASE_URL}/stats`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des statistiques');
    }

    return response.json();
  }

  /**
   * Obtenir la couleur de priorité pour l'UI
   */
  getPriorityColor(priority: string): string {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 border-red-500 text-red-900';
      case 'high':
        return 'bg-orange-100 border-orange-500 text-orange-900';
      case 'medium':
        return 'bg-blue-100 border-blue-500 text-blue-900';
      case 'low':
        return 'bg-gray-100 border-gray-400 text-gray-900';
      default:
        return 'bg-gray-100 border-gray-400 text-gray-900';
    }
  }

  /**
   * Obtenir l'icône de priorité
   */
  getPriorityIcon(priority: string): string {
    switch (priority) {
      case 'critical':
        return '🚨';
      case 'high':
        return '⚠️';
      case 'medium':
        return 'ℹ️';
      case 'low':
        return '📝';
      default:
        return '📋';
    }
  }

  /**
   * Obtenir l'icône de type
   */
  getTypeIcon(type: string): string {
    switch (type) {
      case 'new_booking':
        return '📅';
      case 'payment_failed':
        return '❌';
      case 'payment_reversed':
        return '🚨';
      case 'booking_canceled':
        return '🚫';
      case 'payment_pending_timeout':
        return '⏱️';
      default:
        return '📋';
    }
  }
}

export const notificationService = new NotificationService();
